/**
 * TanStack code-based route tree — the 1:1 replacement for the RR3 `routes.js`
 * tree. Every path, redirect, and index route is preserved so existing URLs,
 * bookmarks, and e2e tests keep working.
 *
 * Conventions (see client/routeHelpers.tsx):
 *  - Leaf route components are wrapped in `withRouter(...)` so they receive the
 *    RR3 prop bag (`params`/`location`/`route`/`routes`) the router used to
 *    inject. Layout routes use `layoutComponent(...)` (injects props + Outlet).
 *  - RR3 `(/:x)` optional params → `{-$x}`. RR3 `<Redirect>`/`<IndexRedirect>` →
 *    `redirectRoute(...)`. RR3 redirect-only `onEnter` → `beforeLoad`.
 *  - Custom RR3 route props (`withWhiteBackground`, `title`, `queryType`, etc.)
 *    → route `staticData`.
 */
import React, {lazy} from 'react';
import {createRootRoute, type AnyRoute} from '@tanstack/react-router';

import {TranslationSandbox} from 'client/TranslationSandbox/TranslationSandbox';
import {requireSessionWithLoginPrompt} from 'lib/decorators/requireSession';
import sessionStore from 'client/Session/SessionStore';
import {getUserTypeRedirectTarget} from 'client/User/UserRedirectUtil';
import {queryTypes} from 'client/SubjectPracticeView/subjectPracticeView.queries';

import {
  createRoute,
  redirect,
  redirectRoute,
  layoutComponent,
  withRouter
} from 'client/routeHelpers';

import App from './App.react';
import Register from 'client/static/UserActions/Register/Register.react';
import ErrorPage from './static/Error/ErrorPage.react';
import ExpiredSessionPage from './static/ExpiredSession/ExpiredSessionPage.react';
import SoftwareConsiderations from './static/SoftwareConsiderations/SoftwareConsiderations.react';

import GlobalAssignments from 'client/Assignments/GlobalAssignments/GlobalAssignments.react';
import Coppa from 'client/Coppa/Coppa.react';
import ConfirmEmail from 'client/static/UserActions/ConfirmEmail/ConfirmEmail.react';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import LogInPage from './LogIn/LogInPage';
import ResetPassword from './ResetPassword/ResetPassword';
import SignUpPage from './SignUp/NewSignUp/SignUpPage';
import SocialLanding from './Social/Landing.react';
import SubjectLibrary from './SubjectLibrary/SubjectLibrary.react';
import {SubjectView} from './SubjectView/V2/SubjectView';
import {subjectPageOnEnter} from './SubjectView/V2/subjectPageOnEnter';
import FRQGuideRouter from './FreeResponseQuestion/FRQGuideRouter';
import FRQViewRouter from './FreeResponseQuestion/FRQViewRouter';
import StudyGuide from './StudyGuide/StudyGuide.react';
import SubjectFAQList from './SubjectFAQList/SubjectFAQList';
import ClassworkChrome from 'client/TeacherDashboard/Layout/ClassworkChrome';
import {ClassworkSubjects} from 'client/TeacherDashboard/ClassworkSubjects/ClassworkSubjects';
import SubjectPracticeView from './SubjectPracticeView/SubjectPracticeView.react';

import {makeMarketplaceRoute} from './Marketplace/dynamic.routes';
import {makeHomeRoute} from './StudentHomepage/routes';
import {makeAssignmentBuilderRoute} from 'client/components/ContentDiscovery/routes';
import {makeSchoolAdminRoute} from './SchoolAdministration/routes';
import {makeClassesRoute} from './Classrooms/routes';
import {makeReportsRoute} from './Reports/routes';
import {makeDennisRoutes} from './Dennis/dynamic.routes';

const Account = lazy(() => import('client/AccountSettings/index.react'));
const AccountProfile = lazy(
  () => import('client/AccountSettings/AccountProfile/AccountProfile.react')
);
const Subscriptions = lazy(
  () => import('client/AccountSettings/Subscriptions/Subscriptions.react')
);
const PasswordAndSettings = lazy(
  () => import('client/AccountSettings/PasswordAndSettings/PasswordAndSettings')
);
const DeactivateAccount = lazy(
  () => import('client/AccountSettings/DeactivateAccount/DeactivateAccount.react')
);
const UserDashboard = lazy(() => import('./UserDashboard/UserDashboard'));
const StudentDetailView = lazy(() => import('./Student/StudentDetail.react'));
const Folders = lazy(() => import('./Folders/Folders.react'));
const AssignmentDrafts = lazy(() => import('client/AssignmentDrafts/AssignmentDrafts.react'));
const TeacherAssignmentPracticeView = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/TeacherAssignmentPracticeView/TeacherAssignmentPracticeView.react')
);
const ViewStudentSubmission = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.react')
);
const ViewStudentSubmissionIndex = lazy(
  () =>
    import('client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmissionIndex.react')
);
const AdaptiveSubjects = lazy(() => import('client/AdaptiveSubjects/AdaptiveSubjects'));
const AdaptiveDiagnosticAssignPage = lazy(
  () => import('client/AdaptiveDiagnostics/AssignPage/AdaptiveDiagnosticAssignPage')
);
const AssignmentPracticeView = lazy(
  () => import('./AssignmentPracticeView/AssignmentPracticeView.react')
);
const FolderPracticeView = lazy(() => import('./FolderPracticeView/FolderPracticeView.react'));
const AdaptivePracticeView = lazy(() => import('./AdaptivePracticeView/AdaptivePracticeView'));
const DiagnosticPracticeView = lazy(
  () => import('./DiagnosticPracticeView/DiagnosticPracticeView')
);
const TeacherDiagnosticManagement = lazy(
  () => import('./AdaptiveDiagnostics/Management/TeacherDiagnosticManagement')
);
const Sandbox = lazy(() => import(/* webpackChunkName: "Sandbox" */ './Sandbox/Sandbox.react'));
const GraphingCalculator = lazy(
  () =>
    import(
      /* webpackChunkName: "GraphingCalculator" */ './GraphingCalculator/GraphingCalculator.react'
    )
);
const CommonBankQuestion = lazy(
  () => import('./components/QuestionTypes/CommonBank/CommonBankQuestion')
);
const OrderingBankQuestion = lazy(
  () => import('./components/QuestionTypes/CommonBank/OrderingBankQuestion')
);
const GeneratedStories = lazy(
  () => import(/* webpackChunkName: "Stories" */ './GeneratedStories/GeneratedStories.react')
);

const AssignmentsRoute = () => (
  <ClassworkChrome>
    <GlobalAssignments />
  </ClassworkChrome>
);
const FoldersRoute = () => (
  <ClassworkChrome>
    <Folders />
  </ClassworkChrome>
);
const TeacherDiagnosticsRoute = () => (
  <ClassworkChrome>
    <TeacherDiagnosticManagement />
  </ClassworkChrome>
);
const ClassworkSubjectsRoute = () => (
  <ClassworkChrome>
    <ClassworkSubjects />
  </ClassworkChrome>
);

// RR3 `getComponent` chooser: students get the practice view, teachers the
// (session-gated) teacher view. Re-evaluated on each render, matching RR3.
const TeacherAssignmentPracticeViewGuarded = requireSessionWithLoginPrompt()(
  TeacherAssignmentPracticeView
);
function AssignmentRouteChooser(props: Record<string, unknown>) {
  const Component = sessionStore.isStudent()
    ? AssignmentPracticeView
    : TeacherAssignmentPracticeViewGuarded;
  return <Component {...props} />;
}

/** RR3 `redirectOnEnterIfAuthenticated` → async beforeLoad. */
async function redirectIfAuthenticated() {
  if (sessionStore.hasValidSession()) {
    const target = await getUserTypeRedirectTarget();
    throw redirect({to: target} as never);
  }
}

/** RR3 subject-page `onEnter` (async redirect to the right tab) → beforeLoad. */
async function subjectPageBeforeLoad({
  params,
  location
}: {
  params: {slug: string};
  location: {pathname: string};
}) {
  let redirectTo: string | undefined;
  await subjectPageOnEnter(
    {params, location},
    (path: string) => {
      redirectTo = path;
    },
    () => undefined
  );
  if (redirectTo) {
    throw redirect({to: redirectTo} as never);
  }
}

export const rootRoute = createRootRoute({
  component: layoutComponent(App),
  notFoundComponent: () => <ErrorPage />
  // RR3 resumed the session in the root route's `onEnter`, which ran ONCE on
  // entering `/`. TanStack's `rootRoute.beforeLoad` instead re-runs on every
  // route match (≈ every navigation), so doing it here would `await resumeSession()`
  // on every SPA nav — a per-navigation `/auth/session` round-trip that stalls
  // navigations (authoring editor / assignment flows time out). Session restore is
  // done ONCE at boot in main.js instead.
});

const r = (config: Record<string, unknown>): AnyRoute =>
  createRoute({getParentRoute: () => rootRoute, ...config} as any) as AnyRoute;

// --- Account (layout with nested settings) ---
const accountRoute = r({
  path: 'account',
  staticData: {name: 'account'},
  component: layoutComponent(Account)
});
accountRoute.addChildren([
  createRoute({
    getParentRoute: () => accountRoute,
    path: 'profile',
    staticData: {name: 'account-profile'},
    component: withRouter(AccountProfile)
  }),
  createRoute({
    getParentRoute: () => accountRoute,
    path: 'access',
    staticData: {name: 'account-access'},
    component: withRouter(Subscriptions)
  }),
  createRoute({
    getParentRoute: () => accountRoute,
    path: 'settings',
    staticData: {name: 'account-settings'},
    component: withRouter(PasswordAndSettings)
  })
]);

// --- /sandbox (container + index) ---
const sandboxRoute = r({path: 'sandbox'});
sandboxRoute.addChildren([
  createRoute({getParentRoute: () => sandboxRoute, path: '/', component: withRouter(Sandbox)})
]);

// --- /calculator (container + index) ---
const graphingCalculatorRoute = r({path: 'calculator'});
graphingCalculatorRoute.addChildren([
  createRoute({
    getParentRoute: () => graphingCalculatorRoute,
    path: '/',
    component: withRouter(GraphingCalculator)
  })
]);

// --- /:slug subject page (layout + nested) ---
const subjectPageRoute = r({
  path: '$slug',
  staticData: {name: 'subject-page'},
  component: layoutComponent(SubjectView),
  beforeLoad: subjectPageBeforeLoad
});
subjectPageRoute.addChildren([
  createRoute({
    getParentRoute: () => subjectPageRoute,
    path: '/',
    staticData: {name: 'study-guide', withWhiteBackground: true},
    component: withRouter(StudyGuide)
  }),
  createRoute({
    getParentRoute: () => subjectPageRoute,
    path: 'free-response',
    staticData: {withWhiteBackground: true},
    component: withRouter(FRQGuideRouter)
  }),
  createRoute({
    getParentRoute: () => subjectPageRoute,
    path: 'assessments',
    staticData: {withWhiteBackground: true},
    component: withRouter(StudyGuide)
  }),
  redirectRoute({
    getParentRoute: () => subjectPageRoute,
    path: 'practice-exams',
    to: '/$slug/assessments'
  }),
  redirectRoute({getParentRoute: () => subjectPageRoute, path: 'faq', to: '/$slug/overview'}),
  createRoute({
    getParentRoute: () => subjectPageRoute,
    path: 'overview',
    staticData: {name: 'subject-overview'},
    component: withRouter(SubjectFAQList)
  }),
  createRoute({
    getParentRoute: () => subjectPageRoute,
    path: 'free-response/$set/{-$question}',
    staticData: {name: 'free-response-view'},
    component: withRouter(FRQViewRouter)
  })
]);

export const routeTree = rootRoute.addChildren([
  // RR3's root `path='/'` had no IndexRoute: at `/` it rendered App chrome with
  // no page content. Reproduce that (rather than TanStack's not-found) with an
  // index route that renders nothing.
  r({path: '/', component: () => null}),
  r({path: 'session/expired', component: withRouter(ExpiredSessionPage)}),
  makeMarketplaceRoute(rootRoute),
  r({path: 'register', component: withRouter(Register)}),
  makeHomeRoute(rootRoute),

  // Assignments
  r({
    path: 'assignments',
    component: withRouter(requireSessionWithLoginPrompt()(AssignmentsRoute))
  }),
  r({
    path: 'diagnostics',
    component: withRouter(requireSessionWithLoginPrompt()(TeacherDiagnosticsRoute))
  }),
  r({
    path: 'class-subjects',
    component: withRouter(requireSessionWithLoginPrompt()(ClassworkSubjectsRoute))
  }),
  r({
    path: 'saved',
    staticData: {withWhiteBackground: true},
    component: withRouter(requireSessionWithLoginPrompt()(FoldersRoute))
  }),
  r({
    path: 'folders',
    staticData: {withWhiteBackground: true},
    component: withRouter(requireSessionWithLoginPrompt()(FoldersRoute))
  }),
  r({
    path: 'drafts',
    staticData: {withWhiteBackground: true},
    component: withRouter(requireSessionWithLoginPrompt()(AssignmentDrafts))
  }),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'assignment/folders/$assignmentId',
    to: '/folder/$assignmentId'
  }),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'assignment/templates/$assignmentId',
    to: '/folder/$assignmentId'
  }),
  r({path: 'assignment/$assignmentId', component: withRouter(AssignmentRouteChooser)}),
  r({
    path: 'classes/$classroomId/assignments/$assignmentId/student-detail',
    component: withRouter(ViewStudentSubmissionIndex)
  }),
  r({
    path: 'classes/$classroomId/assignments/$assignmentId/student-detail/$studentId/view',
    component: withRouter(ViewStudentSubmission)
  }),
  r({
    path: 'classes/$classroomId/assignments/$assignmentId/student-detail/$studentId/question/$questionId/view',
    component: withRouter(ViewStudentSubmission)
  }),

  makeAssignmentBuilderRoute(rootRoute),

  // Folder practice
  r({
    path: 'folder/$folderId',
    component: withRouter(requireSessionWithLoginPrompt()(FolderPracticeView))
  }),

  // Subject practice views (/learn/...)
  r({
    path: 'learn/question/$subjectSlug/{-$questionSlug}',
    staticData: {queryType: queryTypes.singleQuestion},
    component: withRouter(SubjectPracticeView),
    beforeLoad: ({params}: {params: {questionSlug?: string}}) => {
      if (!params.questionSlug) {
        throw redirect({to: '/error'} as never);
      }
    }
  }),
  r({
    path: 'learn/$subjectSlug/$guideLevelSlug/topic-summary',
    staticData: {isTopicSummary: true, queryType: queryTypes.guideLevel},
    component: withRouter(SubjectPracticeView)
  }),
  r({
    path: 'learn/$subjectSlug/$guideLevelSlug',
    staticData: {queryType: queryTypes.guideLevel},
    component: withRouter(SubjectPracticeView)
  }),
  r({
    path: 'learn/$subjectSlug/$guideLevelSlug/$questionSetSlug/{-$questionSlug}',
    staticData: {queryType: queryTypes.guideLevel},
    component: withRouter(SubjectPracticeView)
  }),

  r({path: 'software-considerations', component: withRouter(SoftwareConsiderations)}),
  r({path: 'confirm-email', component: withRouter(ConfirmEmail)}),
  r({
    path: 'reset-password',
    staticData: {withWhiteBackground: true},
    component: withRouter(ResetPassword)
  }),

  redirectRoute({getParentRoute: () => rootRoute, path: 'dashboard', to: '/my-data'}),
  r({
    path: 'my-data',
    staticData: {withWhiteBackground: true},
    component: withRouter(UserDashboard)
  }),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'dashboard/$studentId/$subjectId',
    to: '/my-data/$studentId/$subjectId'
  }),
  r({path: 'my-data/$studentId/$subjectId', component: withRouter(StudentDetailView)}),

  makeSchoolAdminRoute(rootRoute),
  makeClassesRoute(rootRoute),
  makeReportsRoute(rootRoute),

  accountRoute,
  r({
    path: 'account/deactivate',
    staticData: {name: 'account-deactivate'},
    component: withRouter(DeactivateAccount)
  }),

  // Course library
  r({path: 'subjects', component: withRouter(SubjectLibrary)}),
  redirectRoute({getParentRoute: () => rootRoute, path: 'subjects/free', to: '/subjects'}),
  redirectRoute({getParentRoute: () => rootRoute, path: 'subjects/discover', to: '/subjects'}),
  r({path: 'subjects/adaptive-practice', component: withRouter(SubjectLibrary)}),
  r({
    path: 'subjects/class-subjects',
    staticData: {content: 'class-subjects'},
    component: withRouter(SubjectLibrary)
  }),
  r({path: 'subjects/high-school/{-$group}/{-$subgroup}', component: withRouter(SubjectLibrary)}),
  r({path: 'subjects/middle-school/{-$group}/{-$subgroup}', component: withRouter(SubjectLibrary)}),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'subjects/college/social-sciences/{-$subgroup}',
    to: '/subjects/college'
  }),
  r({path: 'subjects/college/{-$group}/{-$subgroup}', component: withRouter(SubjectLibrary)}),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'subjects/professional/{-$group}/{-$subgroup}',
    to: '/subjects/college'
  }),

  // Adaptive practice
  r({path: 'adaptive-subjects/$slug/{-$guideName}', component: withRouter(AdaptiveSubjects)}),
  r({
    path: 'diagnostics/$diagnosticTemplateId/assign/{-$diagnosticId}',
    component: withRouter(AdaptiveDiagnosticAssignPage)
  }),
  r({path: 'adaptive/skill/$skillId', component: withRouter(AdaptivePracticeView)}),
  r({path: 'adaptive/practice/$practiceId', component: withRouter(AdaptivePracticeView)}),
  r({path: 'diagnostics/$diagnosticId', component: withRouter(DiagnosticPracticeView)}),

  // Authoring → Dennis redirects
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'authoring/queue/{-$questionSetId}/{-$questionId}',
    to: '/dennis/content/queue/{-$questionSetId}/{-$questionId}'
  }),
  redirectRoute({
    getParentRoute: () => rootRoute,
    path: 'authoring/$',
    to: '/dennis/content/dashboard'
  }),

  makeDennisRoutes(rootRoute),

  redirectRoute({getParentRoute: () => rootRoute, path: 'batch', to: '/dennis/batch'}),
  redirectRoute({getParentRoute: () => rootRoute, path: 'batch/$a', to: '/dennis/batch/$a'}),
  redirectRoute({getParentRoute: () => rootRoute, path: 'batch/$a/$b', to: '/dennis/batch/$a/$b'}),

  r({path: 'social', component: withRouter(SocialLanding)}),
  r({
    path: 'signup',
    staticData: {name: 'signup'},
    component: withRouter(SignUpPage),
    beforeLoad: redirectIfAuthenticated
  }),
  r({path: 'log-in', component: withRouter(LogInPage), beforeLoad: redirectIfAuthenticated}),
  r({path: 'coppa/confirmation', component: withRouter(Coppa)}),
  r({
    path: 'forgot-password',
    staticData: {withWhiteBackground: true},
    component: withRouter(ForgotPassword)
  }),
  r({path: 'error', component: withRouter(ErrorPage)}),
  r({path: 'translation-sandbox', component: withRouter(TranslationSandbox)}),
  sandboxRoute,
  graphingCalculatorRoute,
  r({path: 'test/commonbank', component: withRouter(CommonBankQuestion)}),
  r({path: 'test/orderingbank', component: withRouter(OrderingBankQuestion)}),
  r({
    path: 'storybook/{-$atomicLevel}/{-$component}/{-$variant}',
    component: withRouter(GeneratedStories)
  }),

  subjectPageRoute
]);
